import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { Download, TrendingUp, DollarSign, Building2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

function MasterReports() {
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('6months');

  useEffect(() => {
    loadReports();
  }, [period]);

  const loadReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const [growthRes, usageRes] = await Promise.all([
        axios.get(`${API}/api/master-admin/reports/growth?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/master-admin/reports/api-usage`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setReports({
        growth: growthRes.data,
        usage: usageRes.data
      });
      setLoading(false);
    } catch (err) {
      console.error('Erro ao carregar relatórios:', err);
      setLoading(false);
    }
  };

  const exportReport = () => {
    const dataStr = JSON.stringify(reports, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-sistema-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios do Sistema</h1>
          <p className="text-gray-500">Análises detalhadas e métricas de desempenho</p>
        </div>
        <div className="flex gap-3">
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none"
          >
            <option value="1month">Último mês</option>
            <option value="3months">Últimos 3 meses</option>
            <option value="6months">Últimos 6 meses</option>
            <option value="1year">Último ano</option>
          </select>
          <button
            onClick={exportReport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700">
            <Download size={18} /> Exportar
          </button>
        </div>
      </div>

      {/* Crescimento de Tenants */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp size={20} className="text-green-600" />
          Crescimento de Empresas
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={reports.growth}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="new_tenants" stroke="#10B981" name="Novas Empresas" />
            <Line type="monotone" dataKey="active_tenants" stroke="#3B82F6" name="Empresas Ativas" />
            <Line type="monotone" dataKey="monthly_revenue" stroke="#8B5CF6" name="Receita Mensal" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Uso da API por Tenant */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Building2 size={20} className="text-blue-600" />
          Uso da API por Empresa
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={reports.usage.slice(0, 10)}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="tenant_name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="total_messages" fill="#3B82F6" name="Mensagens" />
            <Bar dataKey="total_conversations" fill="#10B981" name="Conversas" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Distribuição de Planos */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign size={20} className="text-purple-600" />
          Distribuição de Planos
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={reports.usage.reduce((acc, item) => {
                const plan = item.plan_type || 'other';
                const existing = acc.find(a => a.name === plan);
                if (existing) {
                  existing.value++;
                } else {
                  acc.push({ name: plan, value: 1 });
                }
                return acc;
              }, [])}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, value }) => `${name}: ${value}`}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {reports.usage.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default MasterReports;